import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  TextInput,
  Platform,
  ScrollView,
  Modal,
  Image,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useArticles } from '../contexts/ArticleContext';
import { 
  getCollection, 
  addItemToCollection, 
  removeItemFromCollection,
  updateCollection
} from '../services/collectionsService';
import { getStores } from '../services/storesService';
import { createArticle, updateArticle } from '../services/articlesService';
import { selectAndUploadImage } from '../services/uploadService';

const CollectionDetailScreen = ({ navigation, route }) => {
  const { collectionId, collectionName } = route.params;
  const { articles: allArticles, loadArticles, addArticle } = useArticles();

  // Hilfsfunktion: Formatiert quantity (entfernt .00 bei reinen Zahlen, lässt Text unverändert)
  const formatQuantity = (quantity) => {
    if (!quantity && quantity !== 0) return '1';
    
    const quantityStr = String(quantity);
    
    // Prüfe ob es eine reine Zahl ist (nur Ziffern, optional mit . und Nachkommastellen)
    // WICHTIG: /^\d+(\.\d+)?$/ prüft ob der String nur eine Zahl ist, nicht z.B. "1-2" oder "ca. 500"
    if (/^\d+(\.\d+)?$/.test(quantityStr.trim())) {
      const numValue = parseFloat(quantityStr);
      // Wenn es eine ganze Zahl ist (oder .00 am Ende hat), zeige nur die ganze Zahl
      if (numValue % 1 === 0) {
        return String(Math.floor(numValue));
      }
      // Falls es echte Nachkommastellen hat (z.B. 1.5), zeige sie an
      return String(numValue);
    }
    
    // Ansonsten: Text unverändert zurückgeben (z.B. "1-2", "ca. 500g")
    return quantityStr;
  };
  const [collection, setCollection] = useState(null);
  const [collectionArticleIds, setCollectionArticleIds] = useState(new Set()); // IDs der Artikel im Korb
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Für Suche zum Hinzufügen
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddMode, setShowAddMode] = useState(false); // Modus: Liste anzeigen vs. Hinzufügen
  const [itemsInShoppingLists, setItemsInShoppingLists] = useState(new Set()); // Artikel die in Einkaufslisten sind
  
  // Für neues Artikel-Modal
  const [editingArticle, setEditingArticle] = useState(null);
  const [newTagText, setNewTagText] = useState('');
  const [stores, setStores] = useState([]);
  const [defaultStore, setDefaultStore] = useState(null);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [lastUsedStores, setLastUsedStores] = useState({}); // {articleId: storeId}
  
  // Für Bildanzeige
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  
  // Für Rezept/Notizen-Modal
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesText, setNotesText] = useState('');

  // Laden des zuletzt verwendeten Ladens pro Artikel
  const loadLastUsedStores = async () => {
    try {
      const storesData = await AsyncStorage.getItem('lastUsedStoresPerArticle');
      if (storesData) {
        return JSON.parse(storesData);
      }
      return {};
    } catch (error) {
      console.error('Fehler beim Laden der Ladenzuordnungen:', error);
      return {};
    }
  };

  // Collection und Daten laden
  const loadData = async () => {
    try {
      setLoading(true);
      const [collectionResult, storesResult, lastUsedStoresData] = await Promise.all([
        getCollection(collectionId),
        getStores(),
        loadLastUsedStores()
      ]);

      if (collectionResult.success) {
        setCollection(collectionResult.data);
        // Setze IDs der Artikel im Korb
        const articleIds = new Set();
        if (collectionResult.data.items && Array.isArray(collectionResult.data.items)) {
          collectionResult.data.items.forEach(item => {
            if (item.article_id) {
              articleIds.add(String(item.article_id)); // Sicherstellen, dass es ein String ist
            }
          });
        }
        setCollectionArticleIds(articleIds);
        // Setze Beschreibung für Notizen-Modal
        setNotesText(collectionResult.data.description || '');
      }

      if (storesResult.success) {
        setStores(storesResult.data);
        if (storesResult.data.length > 0) {
          setDefaultStore(storesResult.data[0]);
        }
      }

      if (lastUsedStoresData) {
        setLastUsedStores(lastUsedStoresData);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      if (Platform.OS === 'web') {
        window.alert('Fehler beim Laden der Daten');
      } else {
        Alert.alert('Fehler', 'Fehler beim Laden der Daten');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Einkaufslisten-Status laden
  const loadItemsInShoppingLists = async () => {
    try {
      const { getAllShoppingLists } = await import('../services/shoppingService');
      const result = await getAllShoppingLists();
      
      if (result.success) {
        const articleIds = new Set();
        
        result.data.forEach(store => {
          if (store.items) {
            store.items.forEach(item => {
              if (item.article_id) {
                articleIds.add(item.article_id);
              }
            });
          }
        });
        
        setItemsInShoppingLists(articleIds);
      }
    } catch (error) {
      console.error('Error loading shopping list items:', error);
    }
  };

  // Einkaufslisten-Status beim Laden der Artikel aktualisieren
  useEffect(() => {
    if (allArticles.length > 0) {
      loadItemsInShoppingLists();
    }
  }, [allArticles]);

  // Daten beim Fokus laden
  useFocusEffect(
    useCallback(() => {
      loadData();
      loadArticles();
    }, [collectionId])
  );

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await loadArticles();
    await loadItemsInShoppingLists();
  };

  // Artikel im Korb (für die Anzeige)
  const collectionArticles = useMemo(() => {
    return allArticles.filter(article => collectionArticleIds.has(article.id));
  }, [allArticles, collectionArticleIds]);

  // Gefilterte Artikel für Suche (zum Hinzufügen) - zeigt ALLE Artikel mit Suchfilter
  const filteredArticlesForAdd = useMemo(() => {
    // Wenn keine Suche aktiv ist, zeige alle Artikel
    if (!searchQuery.trim()) {
      return [...allArticles]; // Kopie erstellen
    }
    
    const query = searchQuery.toLowerCase().trim();
    return allArticles.filter(article => {
      // Suche in Name
      if (article.name.toLowerCase().includes(query)) {
        return true;
      }
      
      // Suche in Tags
      if (article.default_tags && article.default_tags.some(tag => 
        tag.toLowerCase().includes(query)
      )) {
        return true;
      }
      
      // Suche in Notizen
      if (article.notes && article.notes.toLowerCase().includes(query)) {
        return true;
      }
      
      return false;
    });
  }, [allArticles, searchQuery]);

  // Artikel zum Korb hinzufügen/entfernen (Toggle)
  const handleToggleCollection = async (article) => {
    const articleIdStr = String(article.id);
    const isInCollection = collectionArticleIds.has(articleIdStr);
    
    try {
      if (isInCollection) {
        // Artikel entfernen
        const collectionItem = collection?.items.find(item => String(item.article_id) === articleIdStr);
        if (collectionItem) {
          await removeItemFromCollectionAsync(collectionId, collectionItem.id, article);
        }
      } else {
        // Artikel hinzufügen
        const result = await addItemToCollection(collectionId, article.id);
        if (result.success) {
          // Lokale State aktualisieren
          setCollectionArticleIds(prev => new Set([...prev, articleIdStr]));
          setCollection(prev => ({
            ...prev,
            items: [...prev.items, { article_id: article.id, id: result.data.id }],
            item_count: prev.item_count + 1
          }));
        } else {
          if (Platform.OS === 'web') {
            window.alert('Fehler: ' + result.error);
          } else {
            Alert.alert('Fehler', result.error);
          }
        }
      }
    } catch (error) {
      console.error('Fehler beim Toggle:', error);
      if (Platform.OS === 'web') {
        window.alert('Fehler beim Ändern des Artikels');
      } else {
        Alert.alert('Fehler', 'Fehler beim Ändern des Artikels');
      }
    }
  };

  // Artikel aus Korb entfernen
  const handleRemoveFromCollection = async (article) => {
    const message = `"${article.name}" aus dem Korb entfernen?`;
    
    const confirmRemove = () => {
      const collectionItem = collection?.items.find(item => item.article_id === article.id);
      if (collectionItem) {
        removeItemFromCollectionAsync(collectionId, collectionItem.id, article);
      }
    };

    if (Platform.OS === 'web') {
      if (window.confirm(message)) {
        confirmRemove();
      }
    } else {
      Alert.alert(
        'Artikel entfernen',
        message,
        [
          { text: 'Abbrechen', style: 'cancel' },
          {
            text: 'Entfernen',
            style: 'destructive',
            onPress: confirmRemove,
          },
        ]
      );
    }
  };

  const removeItemFromCollectionAsync = async (collectionId, itemId, article) => {
    try {
      const result = await removeItemFromCollection(collectionId, itemId);
      if (result.success) {
        // Lokale State aktualisieren
        const articleIdStr = String(article.id);
        setCollectionArticleIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(articleIdStr);
          return newSet;
        });
        setCollection(prev => ({
          ...prev,
          items: prev.items.filter(item => item.id !== itemId),
          item_count: prev.item_count - 1
        }));
      } else {
        if (Platform.OS === 'web') {
          window.alert('Fehler: ' + result.error);
        } else {
          Alert.alert('Fehler', result.error);
        }
      }
    } catch (error) {
      console.error('Fehler beim Entfernen:', error);
      if (Platform.OS === 'web') {
        window.alert('Fehler beim Entfernen des Artikels');
      } else {
        Alert.alert('Fehler', 'Fehler beim Entfernen des Artikels');
      }
    }
  };

  // Neuen Artikel aus Suchbegriff erstellen - Modal öffnen
  const createArticleFromSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    // Öffne das Modal mit vorausgefüllten Daten für einen neuen Artikel
    setEditingArticle({
      id: null,
      name: searchQuery.trim(),
      quantity: '1',
      unit: 'Stück',
      tags: [],
      notes: '',
      storeId: defaultStore?.id || null,
      isNew: true
    });
  };

  // Bild auswählen und hochladen
  const handleSelectImage = async () => {
    if (!editingArticle) return;
    
    try {
      setEditingArticle(prev => ({ ...prev, uploadingImage: true }));
      
      const result = await selectAndUploadImage();
      
      if (result && result.success && result.imageUrl) {
        setEditingArticle(prev => ({ ...prev, imageUrl: result.imageUrl, uploadingImage: false }));
        if (Platform.OS === 'web') {
          window.alert('Bild wurde erfolgreich hochgeladen');
        } else {
          Alert.alert('Erfolg', 'Bild wurde erfolgreich hochgeladen');
        }
      } else if (result && result.imageUrl) {
        // Fallback für direkte URL
        setEditingArticle(prev => ({ ...prev, imageUrl: result.imageUrl, uploadingImage: false }));
      } else {
        setEditingArticle(prev => ({ ...prev, uploadingImage: false }));
      }
    } catch (error) {
      console.error('Fehler beim Auswählen des Bildes:', error);
      setEditingArticle(prev => ({ ...prev, uploadingImage: false }));
      if (Platform.OS === 'web') {
        window.alert('Fehler beim Hochladen des Bildes');
      } else {
        Alert.alert('Fehler', 'Fehler beim Hochladen des Bildes');
      }
    }
  };

  // Artikel aus Einkaufsliste entfernen
  const handleRemoveFromShoppingList = async (article) => {
    try {
      // Finde den Artikel in allen Einkaufslisten
      const { getAllShoppingLists, deleteItem } = await import('../services/shoppingService');
      const result = await getAllShoppingLists();
      
      if (result.success) {
        for (const store of result.data) {
          if (store.items) {
            const itemToRemove = store.items.find(item => item.article_id === article.id);
            if (itemToRemove) {
              // Entferne den Artikel aus der Einkaufsliste
              const deleteResult = await deleteItem(itemToRemove.id);
              
              if (deleteResult.success) {
                // Aktualisiere die lokalen States SOFORT
                setItemsInShoppingLists(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(String(article.id));
                  return newSet;
                });
                // Einkaufslisten-Status aktualisieren
                await loadItemsInShoppingLists();
                return; // Nur den ersten gefundenen Artikel entfernen
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error removing article from shopping list:', error);
      if (Platform.OS === 'web') {
        window.alert('Fehler beim Entfernen des Artikels');
      } else {
        Alert.alert('Fehler', 'Fehler beim Entfernen des Artikels');
      }
    }
  };

  // Artikel zur Einkaufsliste hinzufügen/entfernen (Toggle)
  const handleToggleShoppingList = async (article) => {
    const articleIdStr = String(article.id);
    const isInShoppingList = itemsInShoppingLists.has(articleIdStr);
    
    if (isInShoppingList) {
      // Artikel entfernen
      await handleRemoveFromShoppingList(article);
    } else {
      // Artikel hinzufügen
      await handleAddToShoppingList(article);
    }
  };

  // Artikel zur Einkaufsliste hinzufügen
  const handleAddToShoppingList = async (article) => {
    // Laden für diesen Artikel bestimmen
    const storeId = article.last_used_store_id || lastUsedStores[article.id];
    const selectedStore = storeId ? stores.find(s => s.id === storeId) : null;
    
    // Fallback: Standard-Laden verwenden wenn kein spezifischer Laden gewählt
    const storeToUse = selectedStore || defaultStore;
    
    if (!storeToUse) {
      if (Platform.OS === 'web') {
        window.alert('Fehler: Kein Laden verfügbar');
      } else {
        Alert.alert('Fehler', 'Kein Laden verfügbar');
      }
      return;
    }

    try {
      const quantity = article.default_quantity?.toString() || '1';
      const itemData = {
        article_id: article.id,
        quantity: quantity,
        unit: article.default_unit || 'Stück',
        tags: article.default_tags || [],
        notes: article.notes || null
      };

      // Artikel zum ausgewählten Laden hinzufügen
      const { addItemToList } = await import('../services/shoppingService');
      const result = await addItemToList(storeToUse.id, itemData);
      
      if (result.success) {
        // Artikel als "in Einkaufsliste" markieren
        setItemsInShoppingLists(prev => new Set([...prev, String(article.id)]));
        
        // Lade Ladenzuordnung speichern
        try {
          const lastUsedStoresData = await AsyncStorage.getItem('lastUsedStoresPerArticle');
          const lastUsedStores = lastUsedStoresData ? JSON.parse(lastUsedStoresData) : {};
          lastUsedStores[article.id] = storeToUse.id;
          await AsyncStorage.setItem('lastUsedStoresPerArticle', JSON.stringify(lastUsedStores));
          setLastUsedStores(lastUsedStores);
        } catch (error) {
          console.error('Fehler beim Speichern der Ladenzuordnung:', error);
        }
        
        // Einkaufslisten-Status aktualisieren
        await loadItemsInShoppingLists();
      } else {
        if (Platform.OS === 'web') {
          window.alert('Fehler: ' + result.error);
        } else {
          Alert.alert('Fehler', result.error);
        }
      }
    } catch (error) {
      console.error('Fehler beim Hinzufügen zur Einkaufsliste:', error);
      if (Platform.OS === 'web') {
        window.alert('Fehler beim Hinzufügen zur Einkaufsliste');
      } else {
        Alert.alert('Fehler', 'Fehler beim Hinzufügen zur Einkaufsliste');
      }
    }
  };

  // Artikel in Korb-Liste rendern (wie ArticlesScreen)
  const renderCollectionArticle = ({ item }) => {
    const itemIdStr = String(item.id);
    const isInShoppingList = itemsInShoppingLists.has(itemIdStr);
    
    // Finde den zugeordneten Laden
    const articleStore = item.last_used_store_id 
      ? stores.find(s => s.id === item.last_used_store_id) 
      : null;

    return (
      <View style={[
        styles.articleCard,
        isInShoppingList && styles.articleCardInShoppingList
      ]}>
        <TouchableOpacity
          style={styles.articleContent}
          onPress={() => {
            // Bearbeiten-Modus öffnen
            setEditingArticle({
              id: item.id,
              name: item.name,
              quantity: formatQuantity(item.default_quantity),
              unit: item.default_unit || '',
              tags: item.default_tags || [],
              notes: item.notes || '',
              storeId: item.last_used_store_id || null,
              imageUrl: item.image_url || '',
              uploadingImage: false
            });
          }}
        >
          <View style={styles.articleMainRow}>
            <View style={styles.articleTextContainer}>
              <View style={styles.articleNameContainer}>
                <View style={styles.articleNameRow}>
                  <Text style={styles.articleName} numberOfLines={2}>
                    {item.name}
                  </Text>
                  {item.image_url && (
                    <TouchableOpacity
                      style={styles.imageIconButton}
                      onPress={() => {
                        setSelectedImageUrl(item.image_url);
                        setShowImageModal(true);
                      }}
                    >
                      <Ionicons name="image" size={16} color="#666" />
                    </TouchableOpacity>
                  )}
                </View>
                
                {/* Tags unter dem Artikelnamen */}
                {item.default_tags && item.default_tags.length > 0 && (
                  <View style={styles.tagsContainer}>
                    {item.default_tags.slice(0, 2).map((tag, index) => (
                      <View key={index} style={styles.tag}>
                        <Text style={styles.tagText}>{tag}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
              
              {item.notes && (
                <Text style={styles.articleNotes} numberOfLines={1}>
                  {item.notes}
                </Text>
              )}
            </View>
          </View>
        </TouchableOpacity>
        
        <View style={styles.articleActions}>
          {/* Laden-Feld */}
          <View style={styles.storeField}>
            <Text style={styles.storeFieldText} numberOfLines={1}>
              {articleStore ? articleStore.name : 'Kein Laden'}
            </Text>
          </View>
          
          {/* Menge-Feld (Zahl über Einheit) */}
          <View style={styles.quantityField}>
            <View style={styles.quantityContainer}>
              <Text style={styles.quantityText}>{formatQuantity(item.default_quantity)}</Text>
              <Text style={styles.unitText}>{item.default_unit}</Text>
            </View>
          </View>
          
          {/* Einkaufsliste-Button (Toggle: hinzufügen/entfernen) */}
          <TouchableOpacity
            style={styles.shoppingListButton}
            onPress={() => handleToggleShoppingList(item)}
          >
            {isInShoppingList ? (
              <Ionicons name="cart" size={20} color="#4CAF50" />
            ) : (
              <Ionicons name="cart-outline" size={20} color="#2E7D32" />
            )}
          </TouchableOpacity>
          
          {/* Mülleimer-Icon zum Entfernen */}
          <TouchableOpacity
            style={styles.deleteButton}
            onPress={() => handleRemoveFromCollection(item)}
          >
            <Ionicons name="trash-outline" size={18} color="#d32f2f" />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // Artikel in Suchliste rendern (zum Hinzufügen) - wie QuickListScreen/ArticlesScreen
  const renderAddArticle = ({ item }) => {
    // Sicherstellen, dass beide IDs als String verglichen werden
    const itemIdStr = String(item.id);
    const isInCollection = collectionArticleIds.has(itemIdStr);
    const isInShoppingList = itemsInShoppingLists.has(itemIdStr);
    
    // Finde den zugeordneten Laden
    const articleStore = item.last_used_store_id 
      ? stores.find(s => s.id === item.last_used_store_id) 
      : null;

    // WICHTIG: Nur nach collectionArticleIds markieren, NICHT nach itemsInShoppingLists
    return (
      <View style={[
        styles.addArticleCard,
        isInCollection && styles.addArticleCardAdded // Nur wenn im Korb
      ]}>
        <TouchableOpacity
          style={styles.addArticleContent}
          onPress={() => handleToggleCollection(item)}
        >
          <View style={styles.addArticleMainRow}>
            <View style={styles.addArticleTextContainer}>
              <View style={styles.addArticleNameContainer}>
                <View style={styles.addArticleNameRow}>
                  <Text style={[
                    styles.addArticleName,
                    isInCollection && styles.addArticleNameAdded
                  ]} numberOfLines={2}>
                    {item.name}
                  </Text>
                  {isInShoppingList && (
                    <View style={styles.shoppingListIndicator}>
                      <Ionicons name="cart" size={16} color="#2E7D32" />
                    </View>
                  )}
                  {item.image_url && (
                    <TouchableOpacity
                      style={styles.addArticleImageIconButton}
                      onPress={() => {
                        setSelectedImageUrl(item.image_url);
                        setShowImageModal(true);
                      }}
                    >
                      <Ionicons name="image" size={16} color="#666" />
                    </TouchableOpacity>
                  )}
                </View>
                
                {/* Tags unter dem Artikelnamen */}
                {item.default_tags && item.default_tags.length > 0 && (
                  <View style={styles.addArticleTagsContainer}>
                    {item.default_tags.slice(0, 2).map((tag, index) => (
                      <View key={index} style={styles.addArticleTag}>
                        <Text style={styles.addArticleTagText}>{tag}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
              
              {item.notes && (
                <Text style={styles.addArticleNotes} numberOfLines={1}>
                  {item.notes}
                </Text>
              )}
            </View>
          </View>
        </TouchableOpacity>
        
        <View style={styles.addArticleActions}>
          {/* Laden-Feld */}
          <View style={styles.addArticleStoreField}>
            <Text style={styles.addArticleStoreFieldText} numberOfLines={1}>
              {articleStore ? articleStore.name : 'Kein Laden'}
            </Text>
          </View>
          
          {/* Menge-Feld */}
          <View style={styles.addArticleQuantityField}>
            <View style={styles.addArticleQuantityContainer}>
              <Text style={styles.addArticleQuantityText}>
                {formatQuantity(item.default_quantity)}
              </Text>
              <Text style={styles.addArticleUnitText}>
                {item.default_unit}
              </Text>
            </View>
          </View>
          
          {/* Hinzufügen/Entfernen-Button */}
          <TouchableOpacity
            style={[
              styles.addArticleToggleButton,
              isInCollection && styles.addArticleToggleButtonAdded
            ]}
            onPress={() => handleToggleCollection(item)}
          >
            {isInCollection ? (
              <Ionicons name="checkmark-circle" size={24} color="#4CAF50" />
            ) : (
              <Ionicons name="add-circle-outline" size={24} color="#2E7D32" />
            )}
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header mit Collection-Info und Add-Button */}
      <View style={styles.headerContainer}>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
          <Text style={styles.headerTitle}>{collectionName}</Text>
          {collection && collection.description && (
            <TouchableOpacity
              onPress={() => {
                setNotesText(collection.description || '');
                setShowNotesModal(true);
              }}
            >
              <Ionicons name="document-text" size={20} color="#2E7D32" />
            </TouchableOpacity>
          )}
        </View>
        <View style={styles.headerInfo}>
          <Text style={styles.headerSubtitle}>
            {collection ? `${collection.items.length} Artikel` : 'Lädt...'}
          </Text>
          <View style={styles.headerButtons}>
            <TouchableOpacity
              style={styles.addButton}
              onPress={() => {
                if (collection) {
                  setNotesText(collection.description || '');
                  setShowNotesModal(true);
                }
              }}
            >
              <Ionicons 
                name="document-text-outline" 
                size={20} 
                color="#2E7D32" 
              />
              <Text style={styles.addButtonText}>
                Notizen
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.addButton, showAddMode && styles.addButtonActive]}
              onPress={() => {
                setShowAddMode(!showAddMode);
                setSearchQuery('');
              }}
            >
              <Ionicons 
                name={showAddMode ? "close" : "add"} 
                size={20} 
                color={showAddMode ? "#fff" : "#2E7D32"} 
              />
              <Text style={[styles.addButtonText, showAddMode && styles.addButtonTextActive]}>
                {showAddMode ? 'Abbrechen' : 'Artikel hinzufügen'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Suchleiste (immer im Add-Modus) */}
      {showAddMode && (
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search-outline" size={20} color="#666" style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="Artikel suchen..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor="#999"
              autoFocus={showAddMode}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity
                onPress={() => setSearchQuery('')}
                style={styles.clearButton}
              >
                <Ionicons name="close-circle" size={20} color="#666" />
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}

      {/* Liste: Entweder Korb-Artikel oder alle Artikel im Add-Modus */}
      {showAddMode ? (
        <FlatList
          data={filteredArticlesForAdd}
          renderItem={renderAddArticle}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl 
              refreshing={refreshing} 
              onRefresh={onRefresh}
              colors={['#2E7D32']}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="search-outline" size={64} color="#ccc" />
              <Text style={styles.emptyText}>
                {searchQuery ? 'Keine Artikel gefunden' : 'Artikel suchen'}
              </Text>
              <Text style={styles.emptySubtext}>
                {searchQuery 
                  ? 'Versuche einen anderen Suchbegriff oder erstelle einen neuen Artikel'
                  : 'Gebe einen Suchbegriff ein oder blättere durch alle Artikel'
                }
              </Text>
              {searchQuery && (
                <TouchableOpacity
                  style={styles.createArticleButton}
                  onPress={createArticleFromSearch}
                >
                  <Ionicons name="add-circle-outline" size={20} color="#fff" />
                  <Text style={styles.createArticleButtonText}>
                    "{searchQuery}" erstellen
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          }
          ListFooterComponent={
            searchQuery && searchQuery.trim() ? (
              <View style={styles.createArticleFooter}>
                <TouchableOpacity
                  style={styles.createArticleFooterButton}
                  onPress={createArticleFromSearch}
                >
                  <Ionicons name="add-circle-outline" size={20} color="#2E7D32" />
                  <Text style={styles.createArticleFooterText}>
                    "{searchQuery}" als neuen Artikel erstellen
                  </Text>
                </TouchableOpacity>
              </View>
            ) : null
          }
        />
      ) : (
        <FlatList
          data={collectionArticles}
          renderItem={renderCollectionArticle}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl 
              refreshing={refreshing} 
              onRefresh={onRefresh}
              colors={['#2E7D32']}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="basket-outline" size={64} color="#ccc" />
              <Text style={styles.emptyText}>Keine Artikel im Korb</Text>
              <Text style={styles.emptySubtext}>
                Füge Artikel zu deinem Korb hinzu
              </Text>
              <TouchableOpacity
                style={styles.addButton}
                onPress={() => setShowAddMode(true)}
              >
                <Ionicons name="add" size={20} color="#2E7D32" />
                <Text style={styles.addButtonText}>Artikel hinzufügen</Text>
              </TouchableOpacity>
            </View>
          }
        />
      )}
      
      {/* Modal für neues Artikel */}
      <Modal
        visible={editingArticle !== null}
        transparent
        animationType="slide"
        onRequestClose={() => setEditingArticle(null)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            {/* Artikelname - editierbar bei neuen Artikeln */}
            {editingArticle && editingArticle.isNew ? (
              <TextInput
                style={styles.modalTitleInput}
                value={editingArticle.name}
                onChangeText={(text) => setEditingArticle({...editingArticle, name: text})}
                placeholder="Artikelname"
                autoFocus
              />
            ) : (
              <Text style={styles.modalTitle}>{editingArticle?.name}</Text>
            )}

            {/* Laden-Auswahl */}
            <View style={styles.modalSection}>
              <Text style={styles.modalLabel}>Laden:</Text>
              <TouchableOpacity
                style={styles.storeSelectButton}
                onPress={() => setShowStoreModal(true)}
              >
                <Text style={styles.storeSelectText}>
                  {editingArticle?.storeId ? stores.find(s => s.id === editingArticle.storeId)?.name || 'Laden wählen' : 'Kein Laden (Standard)'}
                </Text>
                <Ionicons name="chevron-down" size={20} color="#666" />
              </TouchableOpacity>
            </View>

            {/* Menge-Eingabe */}
            <View style={styles.modalSection}>
              <View style={styles.quantityLabelRow}>
                <Text style={styles.modalLabel}>Menge:</Text>
                <TextInput
                  style={styles.unitInput}
                  value={editingArticle?.unit || ''}
                  onChangeText={(text) => setEditingArticle({...editingArticle, unit: text})}
                  placeholder="Einheit"
                />
              </View>
              <View style={styles.quantityInputContainer}>
                <TextInput
                  style={styles.quantityInput}
                  value={editingArticle?.quantity?.toString() || ''}
                  onChangeText={(text) => {
                    // Erlaube beliebigen Text für Menge (z.B. "2-3", "ca. 500g", etc.)
                    setEditingArticle({...editingArticle, quantity: text});
                  }}
                  placeholder="z.B. 2, 2-3, ca. 500"
                />
                <View style={styles.quickInputButtons}>
                  {[1, 2, 3, 4, 5, 6].map(number => (
                    <TouchableOpacity
                      key={number}
                      style={styles.quickInputButton}
                      onPress={() => setEditingArticle({...editingArticle, quantity: number.toString()})}
                    >
                      <Text style={styles.quickInputButtonText}>{number}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </View>

            {/* Bild */}
            <View style={styles.modalSection}>
              <Text style={styles.modalLabel}>Bild:</Text>
              <TouchableOpacity
                style={styles.imageSelectButton}
                onPress={handleSelectImage}
                disabled={editingArticle?.uploadingImage}
              >
                {editingArticle?.uploadingImage ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <ActivityIndicator size="small" color="#2E7D32" style={{ marginRight: 8 }} />
                    <Text style={styles.imageSelectText}>Lädt...</Text>
                  </View>
                ) : editingArticle?.imageUrl ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={styles.imageSelectText}>Bild ausgewählt</Text>
                    <Image 
                      source={{ uri: editingArticle.imageUrl }} 
                      style={{ width: 30, height: 30, borderRadius: 4, marginLeft: 8 }}
                    />
                  </View>
                ) : (
                  <Text style={styles.imageSelectText}>Bild auswählen</Text>
                )}
                {!editingArticle?.uploadingImage && (
                  <Ionicons name="image-outline" size={20} color="#666" />
                )}
              </TouchableOpacity>
            </View>

            {/* Notizen */}
            <View style={styles.modalSection}>
              <Text style={styles.modalLabel}>Notizen:</Text>
              <TextInput
                style={styles.notesInput}
                value={editingArticle?.notes || ''}
                onChangeText={(text) => setEditingArticle({...editingArticle, notes: text})}
                placeholder="Notizen zum Artikel..."
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Tags-Eingabe */}
            <View style={styles.modalSection}>
              <Text style={styles.modalLabel}>Tags:</Text>
              <View style={styles.tagsInputContainer}>
                {editingArticle?.tags?.map((tag, index) => (
                  <View key={index} style={styles.tagInputItem}>
                    <Text style={styles.tagInputText}>{tag}</Text>
                    <TouchableOpacity
                      style={styles.tagRemoveButton}
                      onPress={() => {
                        const newTags = editingArticle?.tags?.filter((_, i) => i !== index) || [];
                        setEditingArticle({...editingArticle, tags: newTags});
                      }}
                    >
                      <Ionicons name="close" size={16} color="#666" />
                    </TouchableOpacity>
                  </View>
                ))}
                <TextInput
                  style={styles.tagInput}
                  value={newTagText}
                  onChangeText={setNewTagText}
                  placeholder="Tag hinzufügen..."
                  onSubmitEditing={() => {
                    if (newTagText.trim() && !editingArticle?.tags?.includes(newTagText.trim())) {
                      setEditingArticle({
                        ...editingArticle,
                        tags: [...(editingArticle?.tags || []), newTagText.trim()]
                      });
                      setNewTagText('');
                    }
                  }}
                />
              </View>
            </View>

            {/* Buttons */}
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setEditingArticle(null);
                  setNewTagText('');
                }}
              >
                <Text style={styles.cancelButtonText}>Abbrechen</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={async () => {
                  try {
                    // Prüfen ob neuer oder bestehender Artikel
                    const isNewArticle = editingArticle?.isNew || false;
                    
                    // Tags verarbeiten: Bestehende Tags + eventuell noch nicht gespeicherter Text
                    let finalTags = [...(editingArticle?.tags || [])];
                    if (newTagText.trim()) {
                      finalTags.push(newTagText.trim());
                      setNewTagText(''); // Eingabefeld leeren
                    }

                    if (isNewArticle) {
                      // Neuer Artikel erstellen
                      const result = await createArticle({
                        name: editingArticle?.name || '',
                        default_quantity: editingArticle?.quantity || 1,
                        default_unit: editingArticle?.unit || 'Stück',
                        default_tags: finalTags,
                        notes: editingArticle?.notes || '',
                        image_url: editingArticle?.imageUrl || '',
                        last_used_store_id: editingArticle?.storeId || null
                      });

                      if (result.success) {
                        // Artikel zum Korb hinzufügen
                        await addItemToCollection(collectionId, result.data.id);
                        
                        // Daten neu laden
                        await loadData();
                        await loadArticles();
                        
                        // Modal schließen
                        setEditingArticle(null);
                        setNewTagText('');
                        setSearchQuery('');
                        setShowAddMode(false);
                      } else {
                        if (Platform.OS === 'web') {
                          window.alert('Fehler: ' + result.error);
                        } else {
                          Alert.alert('Fehler', result.error);
                        }
                      }
                    } else {
                      // Bestehenden Artikel aktualisieren
                      const result = await updateArticle(editingArticle?.id, {
                        name: editingArticle?.name || '',
                        default_quantity: editingArticle?.quantity || 1,
                        default_unit: editingArticle?.unit || 'Stück',
                        default_tags: finalTags,
                        notes: editingArticle?.notes || '',
                        image_url: editingArticle?.imageUrl || '',
                        last_used_store_id: editingArticle?.storeId || null
                      });

                      if (result.success) {
                        // Daten neu laden
                        await loadData();
                        await loadArticles();
                        
                        // Modal schließen
                        setEditingArticle(null);
                        setNewTagText('');
                      } else {
                        if (Platform.OS === 'web') {
                          window.alert('Fehler: ' + result.error);
                        } else {
                          Alert.alert('Fehler', result.error);
                        }
                      }
                    }
                  } catch (error) {
                    console.error('Fehler beim Speichern des Artikels:', error);
                    if (Platform.OS === 'web') {
                      window.alert('Fehler beim Speichern des Artikels');
                    } else {
                      Alert.alert('Fehler', 'Fehler beim Speichern des Artikels');
                    }
                  }
                }}
              >
                <Text style={styles.saveButtonText}>
                  {editingArticle?.isNew ? 'Erstellen' : 'Speichern'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Store Selection Modal */}
      <Modal
        visible={showStoreModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowStoreModal(false)}
      >
        <View style={styles.storeModalOverlay}>
          <View style={styles.storeModalContent}>
            <Text style={styles.storeModalTitle}>Laden auswählen</Text>
            
            <ScrollView style={styles.storeList}>
              <TouchableOpacity
                style={[
                  styles.storeOption,
                  !editingArticle?.storeId && styles.storeOptionSelected
                ]}
                onPress={() => {
                  setEditingArticle(prev => ({...prev, storeId: null}));
                  setShowStoreModal(false);
                }}
              >
                <Text style={styles.storeOptionText}>Kein Laden (Standard)</Text>
                {!editingArticle?.storeId && (
                  <Ionicons name="checkmark" size={20} color="#2E7D32" />
                )}
              </TouchableOpacity>
              
              {stores.map(store => (
                <TouchableOpacity
                  key={store.id}
                  style={[
                    styles.storeOption,
                    editingArticle?.storeId === store.id && styles.storeOptionSelected
                  ]}
                  onPress={() => {
                    setEditingArticle(prev => ({...prev, storeId: store.id}));
                    setShowStoreModal(false);
                  }}
                >
                  <Text style={styles.storeOptionText}>{store.name}</Text>
                  {editingArticle?.storeId === store.id && (
                    <Ionicons name="checkmark" size={20} color="#2E7D32" />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Bild-Anzeige Modal */}
      <Modal
        visible={showImageModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowImageModal(false)}
      >
        <View style={styles.imageModalOverlay}>
          <TouchableOpacity
            style={styles.imageModalCloseButton}
            onPress={() => setShowImageModal(false)}
          >
            <Ionicons name="close" size={24} color="#fff" />
          </TouchableOpacity>
          <Image
            source={{ uri: selectedImageUrl }}
            style={styles.imageModalImage}
            resizeMode="contain"
          />
        </View>
      </Modal>

      {/* Rezept/Notizen Modal */}
      <Modal
        visible={showNotesModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowNotesModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.notesModalContent}>
            <View style={styles.notesModalHeader}>
              <Text style={styles.notesModalTitle}>Rezept / Notizen</Text>
              <TouchableOpacity
                onPress={() => setShowNotesModal(false)}
                style={styles.notesModalCloseButton}
              >
                <Ionicons name="close" size={24} color="#333" />
              </TouchableOpacity>
            </View>
            <ScrollView 
              style={styles.notesModalScrollView}
              contentContainerStyle={styles.notesModalScrollContent}
            >
              <TextInput
                style={styles.notesModalInput}
                value={notesText}
                onChangeText={setNotesText}
                placeholder="Rezept, Notizen oder Anweisungen hier eingeben..."
                placeholderTextColor="#999"
                multiline
                textAlignVertical="top"
                autoFocus
              />
            </ScrollView>
            <View style={styles.notesModalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setShowNotesModal(false);
                  setNotesText(collection?.description || '');
                }}
              >
                <Text style={styles.cancelButtonText}>Abbrechen</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={async () => {
                  try {
                    const result = await updateCollection(collectionId, {
                      description: notesText.trim() || null
                    });
                    
                    if (result.success) {
                      // Collection-Daten aktualisieren
                      setCollection(prev => ({
                        ...prev,
                        description: notesText.trim() || null
                      }));
                      setShowNotesModal(false);
                    } else {
                      if (Platform.OS === 'web') {
                        window.alert('Fehler: ' + result.error);
                      } else {
                        Alert.alert('Fehler', result.error);
                      }
                    }
                  } catch (error) {
                    console.error('Fehler beim Speichern der Notizen:', error);
                    if (Platform.OS === 'web') {
                      window.alert('Fehler beim Speichern der Notizen');
                    } else {
                      Alert.alert('Fehler', 'Fehler beim Speichern der Notizen');
                    }
                  }
                }}
              >
                <Text style={styles.saveButtonText}>Speichern</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerContainer: {
    backgroundColor: '#fff',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  headerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    flexWrap: 'nowrap',
  },
  notesButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f8f0',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#2E7D32',
    marginRight: 8,
    height: 36,
    minWidth: 90,
    zIndex: 10,
    opacity: 1,
  },
  notesButtonText: {
    fontSize: 14,
    color: '#2E7D32',
    fontWeight: '600',
    marginLeft: 6,
  },
  notesButtonTextDisabled: {
    color: '#999',
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
    flexShrink: 1,
    marginRight: 12,
    maxWidth: '40%',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f8f0',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#2E7D32',
    gap: 4,
    marginLeft: 8,
  },
  addButtonActive: {
    backgroundColor: '#2E7D32',
    borderColor: '#2E7D32',
  },
  addButtonText: {
    fontSize: 14,
    color: '#2E7D32',
    fontWeight: '600',
  },
  addButtonTextActive: {
    color: '#fff',
  },
  addButtonDisabled: {
    opacity: 0.5,
  },
  addButtonTextDisabled: {
    color: '#999',
  },
  searchContainer: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    outline: 'none',
    borderWidth: 0,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  clearButton: {
    marginLeft: 8,
    padding: 2,
  },
  listContent: {
    padding: 10,
  },
  articleCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  articleContent: {
    flex: 1,
  },
  articleMainRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  articleTextContainer: {
    flex: 1,
  },
  articleNameContainer: {
    flex: 1,
    marginRight: 8,
  },
  articleNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  articleName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    flexShrink: 1,
    marginRight: 6,
  },
  imageIconButton: {
    padding: 4,
    marginLeft: 8,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  tag: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    marginRight: 4,
  },
  tagText: {
    fontSize: 10,
    color: '#2E7D32',
    fontWeight: '600',
  },
  articleNotes: {
    fontSize: 11,
    color: '#999',
    fontStyle: 'italic',
    marginTop: 4,
  },
  articleActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  storeField: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 4,
    height: 32,
  },
  storeFieldText: {
    fontSize: 10,
    color: '#666',
  },
  quantityField: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 4,
    height: 32,
  },
  quantityContainer: {
    alignItems: 'center',
    marginRight: 2,
  },
  quantityText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    lineHeight: 14,
  },
  unitText: {
    fontSize: 8,
    color: '#666',
    lineHeight: 10,
  },
  deleteButton: {
    padding: 6,
  },
  shoppingListButton: {
    padding: 6,
  },
  articleCardInShoppingList: {
    backgroundColor: '#f0f8f0',
    borderLeftColor: '#2E7D32',
    borderLeftWidth: 4,
  },
  addArticleCard: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  addArticleCardAdded: {
    backgroundColor: '#f0f8f0',
    borderColor: '#4CAF50',
  },
  addArticleContent: {
    flex: 1,
  },
  addArticleMainRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  addArticleTextContainer: {
    flex: 1,
  },
  addArticleNameContainer: {
    flex: 1,
    marginRight: 8,
  },
  addArticleNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  addArticleName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    flexShrink: 1,
    marginRight: 6,
  },
  addArticleNameAdded: {
    color: '#2E7D32',
  },
  shoppingListIndicator: {
    backgroundColor: '#e8f5e9',
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 8,
  },
  addArticleImageIconButton: {
    padding: 4,
    marginLeft: 8,
  },
  addArticleTagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  addArticleTag: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    marginRight: 4,
  },
  addArticleTagText: {
    fontSize: 10,
    color: '#2E7D32',
    fontWeight: '600',
  },
  addArticleNotes: {
    fontSize: 11,
    color: '#999',
    fontStyle: 'italic',
    marginTop: 4,
  },
  addArticleActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  addArticleStoreField: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 4,
    height: 32,
  },
  addArticleStoreFieldText: {
    fontSize: 10,
    color: '#666',
  },
  addArticleQuantityField: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 4,
    height: 32,
  },
  addArticleQuantityContainer: {
    alignItems: 'center',
    marginRight: 2,
  },
  addArticleQuantityText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    lineHeight: 14,
  },
  addArticleUnitText: {
    fontSize: 8,
    color: '#666',
    lineHeight: 10,
  },
  addArticleToggleButton: {
    padding: 4,
  },
  addArticleToggleButtonAdded: {
    // Keine zusätzlichen Styles nötig
  },
  emptyContainer: {
    alignItems: 'center',
    paddingTop: 60,
  },
  emptyText: {
    fontSize: 20,
    color: '#999',
    marginTop: 20,
    fontWeight: '600',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 10,
    textAlign: 'center',
  },
  createArticleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2E7D32',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 16,
    alignSelf: 'center',
  },
  createArticleButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  createArticleFooter: {
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  createArticleFooterButton: {
    backgroundColor: '#fff',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#2E7D32',
  },
  createArticleFooterText: {
    color: '#2E7D32',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  // Modal Styles
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    width: '90%',
    maxHeight: '80%',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
  },
  modalTitleInput: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    paddingBottom: 10,
  },
  modalSection: {
    marginBottom: 20,
  },
  modalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  quantityInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
    width: 80,
    marginRight: 12,
    textAlign: 'center',
  },
  unitInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
    flex: 1,
  },
  quantityLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  quantityInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  quickInputButtons: {
    flexDirection: 'row',
    gap: 4,
  },
  quickInputButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  quickInputButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  imageSelectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  imageSelectText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  notesInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  tagsInputContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minHeight: 50,
  },
  tagInputItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 6,
    marginBottom: 4,
  },
  tagInputText: {
    fontSize: 12,
    color: '#555',
    marginRight: 4,
  },
  tagRemoveButton: {
    padding: 2,
  },
  tagInput: {
    fontSize: 14,
    color: '#333',
    flex: 1,
    minWidth: 100,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  modalButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    marginHorizontal: 5,
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  saveButton: {
    backgroundColor: '#2E7D32',
  },
  cancelButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  storeSelectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  storeSelectText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  storeModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  storeModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '80%',
    maxHeight: '60%',
    maxWidth: 400,
  },
  storeModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    padding: 20,
    textAlign: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  storeList: {
    maxHeight: 300,
  },
  storeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  storeOptionSelected: {
    backgroundColor: '#f0f8f0',
  },
  storeOptionText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  imageModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageModalCloseButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 20,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageModalImage: {
    width: '90%',
    height: '90%',
  },
  // Notes Modal Styles
  notesModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    maxHeight: '80%',
    overflow: 'hidden',
  },
  notesModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  notesModalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  notesModalCloseButton: {
    padding: 4,
  },
  notesModalScrollView: {
    flex: 1,
  },
  notesModalScrollContent: {
    padding: 20,
  },
  notesModalInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    color: '#333',
    minHeight: 200,
    textAlignVertical: 'top',
  },
  notesModalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
});

export default CollectionDetailScreen;
